import { LEAD_CAPTURE_FIELDS, answerLabelForStep } from '@sse/shared';
import '../styles/magnet-answer-settings.css';

const standardField = key => LEAD_CAPTURE_FIELDS.find(field => field.value === key);
const fieldLabel = key => standardField(key)?.label || key;
const choice = step => ['single_select', 'multi_select'].includes(step.step_type);
const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};

// Summaries describe local retention, separately from provider delivery.
export function answerStorageSummary(step) {
  const columns = step.step_columns || [];
  if (!columns.length) return choice(step) ? 'Selection not retained' : 'Choose where to keep this answer';
  if (columns.length > 1) return `Included in ${columns.length} destinations`;
  const key = columns[0];
  if (step.step_answer?.id) return `Additional answer: ${answerLabelForStep(step)}`;
  return key === 'description' || !standardField(key) ? 'Included in lead notes' : `Lead detail: ${fieldLabel(key)}`;
}

function sampleAnswer(step) {
  if (choice(step)) return step.step_options?.find(option => option.label?.trim())?.label || 'A visitor’s selected choice';
  return { email: 'alex@example.com', phone: '+1 555 010 0200', scheduler: 'The visitor’s requested time' }[step.step_type] || 'The visitor’s written answer';
}

export function mountAnswerSettings(panel, field, step, saved, advanced, options = {}) {
  panel.querySelector('.flow-task-help')?.remove();
  const label = field.previousElementSibling;
  const oldHint = field.nextElementSibling;
  const summary = element('section', 'flow-answer-result');
  summary.dataset.answerResult = '';
  summary.append(element('span', 'flow-answer-eyebrow', 'When the visitor submits'));
  const title = element('strong'); title.dataset.answerTitle = ''; summary.append(title);
  const description = element('p'); description.dataset.answerDescription = ''; summary.append(description);
  const destinations = element('ul'); destinations.dataset.answerDestinations = ''; summary.append(destinations);
  const sample = element('div', 'flow-answer-example'); sample.dataset.answerExample = '';
  sample.append(element('span', '', 'Example answer'));
  const value = element('span'); value.dataset.answerExampleValue = ''; sample.append(value); summary.append(sample);
  const caution = element('p', 'flow-answer-caution'); caution.dataset.answerCaution = ''; summary.append(caution);

  const naming = element('section', 'flow-answer-naming'); naming.dataset.answerNaming = '';
  const namingLabel = element('label', 'mag-modal__label', 'Answer label');
  const input = element('input', 'mag-modal__input'); input.type = 'text'; input.maxLength = 120;
  input.dataset.answerLabel = ''; input.id = `answer-label-${step._uid}`; namingLabel.htmlFor = input.id;
  const hint = element('p', 'mag-modal__hint', 'For your team in This inquiry. Created from the question unless you personalize it; visitors still see the question.');
  hint.id = `${input.id}-help`; input.setAttribute('aria-describedby', hint.id);
  const reset = element('button', 'flow-answer-link', 'Use question wording'); reset.type = 'button'; reset.dataset.answerLabelReset = '';
  naming.append(namingLabel, input, hint, reset);

  const chooser = element('details', 'flow-detail flow-answer-chooser'); chooser.dataset.flowDetail = 'destination';
  chooser.open = saved?.destination ?? (!step.step_columns.length && !choice(step));
  chooser.append(element('summary', '', 'Change destination'));
  if (label?.tagName === 'LABEL') { label.textContent = 'Where to keep this answer'; chooser.append(label); }
  chooser.append(field);
  if (oldHint?.classList.contains('mag-modal__hint')) oldHint.remove();
  field.options[0].textContent = choice(step) ? 'Do not retain this selection' : 'Choose a lead detail…';
  for (const option of field.options) {
    if (option.value && option.value !== '__additional_answer__' && !standardField(option.value)) option.textContent = 'Keep current additional answer in notes';
  }
  const replacement = element('p', 'mag-modal__hint'); replacement.dataset.answerReplacement = ''; chooser.append(replacement);
  chooser.append(element('p', 'mag-modal__hint', 'This changes future submitted answers. It does not move previous answers or configure a CRM field.'));
  chooser.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !chooser.open) return;
    event.preventDefault(); event.stopPropagation(); chooser.open = false; chooser.querySelector('summary').focus();
  });

  const keys = element('div', 'flow-answer-keys'); keys.dataset.answerKeys = '';
  advanced.prepend(keys);
  const delivery = element('section', 'flow-answer-delivery'); delivery.dataset.answerDelivery = '';
  if (options.getDeliveryContext) {
    delivery.append(element('strong', '', 'Connected destinations'));
    const status = element('p'); status.dataset.answerDeliveryStatus = ''; delivery.append(status);
    const list = element('ul'); list.dataset.answerDeliveryList = ''; delivery.append(list);
    const check = element('button', 'flow-answer-link', 'Check delivery settings'); check.type = 'button'; check.dataset.answerDeliveryCheck = '';
    check.addEventListener('click', () => options.onRequestDelivery?.({ force: true }));
    const open = element('button', 'flow-answer-link', 'View Lead delivery'); open.type = 'button';
    open.addEventListener('click', () => options.onOpenDelivery?.());
    delivery.append(check, open);
  } else delivery.hidden = true;
  panel.prepend(naming, summary, chooser);
  panel.append(delivery);
  updateAnswerSettings(panel, step, options);
}

export function updateAnswerSettings(root, step, options = {}) {
  const summary = root.querySelector('[data-answer-result]');
  if (!summary) return;
  const columns = step.step_columns || [];
  const naming = root.querySelector('[data-answer-naming]');
  naming.hidden = !options.answerAuthoring || columns.length !== 1;
  const labelInput = naming.querySelector('[data-answer-label]');
  if (document.activeElement !== labelInput) labelInput.value = step.step_answer?.label_mode === 'custom' ? step.step_answer.label : answerLabelForStep(step);
  naming.querySelector('[data-answer-label-reset]').hidden = step.step_answer?.label_mode !== 'custom';
  const field = root.querySelector('.flow-f-field');
  const currentOption = field && [...field.options].find(option => option.value === columns[0]);
  if (currentOption && step.step_answer?.id) {
    currentOption.textContent = 'Additional answer — keep with this inquiry';
    field.querySelector('option[value="__additional_answer__"]')?.remove();
  } else if (options.answerAuthoring && field && !field.querySelector('option[value="__additional_answer__"]')) {
    field.add(new Option('Additional answer — keep with this inquiry', '__additional_answer__'));
  }
  summary.querySelector('[data-answer-title]').textContent = step.step_answer?.id ? 'Included with this inquiry' : answerStorageSummary(step);
  const description = !columns.length
    ? step.step_type === 'single_select' ? 'Visitors can still choose a path. Their selection is not included with the lead.'
      : step.step_type === 'multi_select' ? 'Visitors can select answers and continue to the next question. Their selections are not included with the lead.'
        : 'Choose a lead detail before publishing this question.'
    : columns.length > 1 ? 'The same answer is sent to each destination below.'
      : step.step_answer?.id ? 'Kept with this inquiry without replacing a contact detail. It is also included in lead notes when the conversation is sent.'
      : !standardField(columns[0]) ? 'This is an additional answer, kept in the lead’s notes under its existing storage key.'
        : columns[0] === 'description' ? 'The visitor’s answer becomes part of the lead’s notes.'
          : 'This answer is used for the matching detail on the lead. Existing-lead update rules still apply.';
  summary.querySelector('[data-answer-description]').textContent = description;
  const list = summary.querySelector('[data-answer-destinations]'); list.replaceChildren(); list.hidden = columns.length <= 1;
  for (const key of columns) list.append(element('li', '', standardField(key) ? fieldLabel(key) : `Additional answer in notes (${key})`));
  summary.querySelector('[data-answer-example]').hidden = !columns.length;
  summary.querySelector('[data-answer-example-value]').textContent = `${step.step_answer ? answerLabelForStep(step) + ': ' : ''}${sampleAnswer(step)}`;
  const numeric = columns.some(key => ['annual_revenue', 'employees', 'number_of_locations'].includes(key));
  const caution = summary.querySelector('[data-answer-caution]');
  caution.textContent = numeric ? 'This destination expects a number. Ranges or words may be kept in notes instead of filling the number field.'
    : columns.length ? 'Answers are saved when the visitor finishes and sends the conversation. An ending set to skip sending will not save them.' : '';
  caution.hidden = !caution.textContent;
  const replacement = root.querySelector('[data-answer-replacement]');
  replacement.textContent = columns.length > 1
    ? `Choosing a different destination replaces all ${columns.length} destinations listed above for future answers.`
    : step.step_answer?.id ? 'Changing the destination affects future answers. Existing inquiries keep their original answers and labels.'
    : step.step_type === 'single_select' ? 'Retaining a choice and choosing its next path are independent. Set the next question in Choices & paths.'
      : step.step_type === 'multi_select' ? 'Edit the available answers in Choices. Visitors continue to the next question after selecting their answers.'
      : 'Use the lead detail that matches what this question asks.';
  const keys = root.querySelector('[data-answer-keys]'); keys.replaceChildren(); keys.hidden = !columns.length;
  keys.append(element('strong', '', 'Storage keys'));
  for (const key of columns) keys.append(element('code', '', key));
  keys.append(element('p', 'mag-modal__hint', 'These identifiers are preserved for existing answers, prefill and integrations. Editing the wording does not rename them.'));
  updateAnswerDelivery(root, step, options);
}

export function updateAnswerDelivery(root, step, options = {}) {
  const section = root.querySelector('[data-answer-delivery]');
  if (!section || !options.getDeliveryContext) return;
  section.hidden = !(step.step_columns || []).length;
  const context = options.getDeliveryContext() || { status: 'idle' };
  const status = section.querySelector('[data-answer-delivery-status]');
  const list = section.querySelector('[data-answer-delivery-list]'); list.replaceChildren();
  const check = section.querySelector('[data-answer-delivery-check]');
  check.hidden = context.status === 'ready'; check.disabled = context.status === 'loading';
  status.textContent = context.status === 'loading' ? 'Checking this workspace’s delivery settings…'
    : context.status === 'error' ? 'Delivery settings could not be checked. Your answer destination is unchanged.'
      : 'Check this workspace’s settings to see how lead details and notes are shared.';
  if (context.status !== 'ready') return;
  const connections = context.data?.connections || [];
  if (!connections.length) { status.textContent = 'No CRM connection is configured. Submitted answers can still be retained in Magnet.'; return; }
  status.textContent = 'CRM delivery currently requires an email address. These are configuration details, not delivery confirmations.';
  for (const connection of connections) {
    const names = { hubspot: 'HubSpot', salesforce: 'Salesforce', zoho: 'Zoho CRM', gohighlevel: 'HighLevel' };
    const row = element('li');
    row.append(element('strong', '', names[connection.provider] || connection.provider));
    const fields = connection.capabilities?.fields;
    const selected = new Set(step.step_columns.flatMap(key => key === 'name' ? ['first_name', 'last_name'] : [key]));
    const projections = Array.isArray(fields) ? fields.filter(field => selected.has(field.lead_field)) : [];
    const destination = !Array.isArray(fields) ? 'Field details unavailable. View Lead delivery to check this connection.'
      : projections.length ? `${connection.capabilities.record_type}: ${projections.map(field => field.label).join(', ')}. Lead notes are shared separately.`
        : 'No built-in CRM field for this answer. Only content present in the lead’s notes is shared.';
    row.append(element('span', '', destination));
    if (connection.readiness !== 'available') row.append(element('span', 'flow-answer-caution', 'Connection needs checking before delivery can be expected.'));
    list.append(row);
  }
  list.append(element('li', 'flow-answer-delivery-scope', 'CRM notes use the lead’s full notes. Field choices do not limit which answers appear in notes. Repeat inquiries do not currently create a separate CRM delivery each time.'));
}
